/* eslint-disable max-lines */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';

import { overviewLedgerQueryKeys } from 'lib/constants/queries/queryKeys';
import { iaRa } from 'lib/helpers/utility';
import { useTerminal } from 'lib/hooks/queries/terminals/useTerminal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import { currentPractice } from 'components/practice/practiceState';

import { getMutationFn } from './utils';

export const PaymentContext = createContext();
export const PaymentContextApi = createContext();

export const PaymentContextProvider = ({
  children,
  amount,
  initialMethod,
  invoiceToken,
  patientId,
  appointmentId,
  invoices = [],
  practiceId: practiceIdProp,
  onSuccess = () => {},
  onError = () => {},
  checkin = false
}) => {
  const navigate = useNavigate();
  const practice = useRecoilValue(currentPractice);
  const practiceId = practiceIdProp || practice.id;
  const [postedResponse, setPostedResponse] = useState(null);
  const formikValuesRef = useRef(null);
  const processPaymentRef = useRef(null);
  const terminalIdRef = useRef(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const { isOpen, openModal, closeModal } = useModal(false);

  const formik = useFormik({
    initialValues: {
      amount,
      appliedBalance: 0,
      method: initialMethod,
      methodDetail: null,
      invoices,
      appointmentId,
      surchargeAmount: 0,
      patientId,
      payerId: null,
      invoiceToken,
      saveCard: false,
      paymentOption: null,
      surchargeEnabled: practice?.surcharge && practice?.surcharge_enabled,
      customPaymentAmounts: {}
    },
    enableReinitialize: true
  });

  formikValuesRef.current = formik.values;

  const { data: terminalData, isLoading: isTerminalLoading } = useTerminal({
    params: { practiceId, patientId, attribute: 'online' },
    dependencies: [practiceId, patientId],
    options: {
      enabled: !!practiceId && !!patientId
    }
  });

  const isKiosk = !!localStorage.getItem('myriadIsKiosk');

  useEffect(() => {
    const defaultForCard =
      terminalData?.cardPresentTerminal || terminalData?.onlineTerminal || null;
    const defaultForOnline =
      terminalData?.onlineTerminal || terminalData?.cardPresentTerminal || null;

    setSelectedTerminal((current) => {
      const isCardMethod = formik.values.method === 'card';
      const defaultTerminal = isCardMethod ? defaultForCard : defaultForOnline;
      const currentValidForMethod = current && (isCardMethod ? !current.online : current.online);
      return currentValidForMethod ? current : defaultTerminal;
    });
  }, [terminalData?.onlineTerminal, terminalData?.cardPresentTerminal, formik.values.method]);

  const terminals = iaRa(terminalData?.terminals).filter((t) => t.online);
  const cardPresentTerminals = iaRa(terminalData?.terminals).filter((t) => !t.online);
  const terminalPin = selectedTerminal?.pin;
  const terminalId = selectedTerminal?.id;
  const processor = selectedTerminal?.processor;
  const isFullAppliedBalance = formik.values.appliedBalance > 0 && formik.values.amount <= 0;
  const appliedMethod = isFullAppliedBalance ? 'applied-balance' : formik.values.method;

  terminalIdRef.current = terminalId;

  processPaymentRef.current = getMutationFn(processor, appliedMethod);

  const { mutateAsync, isPending: isCharging } = useCustomMutation({
    mutationFn: (params) => processPaymentRef.current(navigate, params),
    disableAlert: true,
    invalidateQueryKey: ['patientBalance', ...overviewLedgerQueryKeys],
    onSuccess: (params) => onSuccess(params, isFullAppliedBalance),
    handleResponse: onError
  });

  const handlePayment = useCallback(
    async (data) => {
      try {
        if (!processPaymentRef.current) return;

        const finalValues = Object.fromEntries(
          Object.entries(formikValuesRef.current).filter(([_, v]) => v !== undefined && v !== null)
        );

        const response = await mutateAsync({
          token: data?.token,
          ...finalValues,
          ...data,
          terminalId: terminalIdRef.current,
          practiceId,
          checkin
        });

        setPostedResponse(response);
        return response;
      } catch (error) {
        console.error(error);
        onError(true);
      }
    },
    [mutateAsync, practiceId, checkin, onError]
  );

  const togglePaymentMethod = useCallback(
    (key) => {
      const isSameMethod = formik.values.method === key;
      formik.setFieldValue('methodDetail', null);
      formik.setFieldValue('method', isSameMethod ? null : key);
      closeModal();
    },
    [formik.values.method, formik.setFieldValue, closeModal]
  );

  const isLoading = isTerminalLoading || isCharging;

  const isAmountLessThanAppliedBalance =
    formik.values.amount <= 0 && formik.values.appliedBalance <= 0;

  const hasValidPaymentMethod = formik.values.method || isFullAppliedBalance;

  const isDisabled =
    !hasValidPaymentMethod ||
    isLoading ||
    !processPaymentRef.current ||
    isAmountLessThanAppliedBalance;

  const disabledTooltip =
    (isAmountLessThanAppliedBalance && 'Amount must be greater than 0') ||
    (!appliedMethod && 'Please select a payment method') ||
    (!processPaymentRef.current && 'Payment method not supported') ||
    (isLoading && 'Processing payment...') ||
    '';

  const contextValue = useMemo(
    () => ({
      values: formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      processor,
      terminalPin,
      terminalId,
      postedResponse,
      isOpen,
      isTerminalLoading,
      isCharging,
      terminal: selectedTerminal,
      isFullAppliedBalance,
      practiceId,
      terminals,
      terminalData,
      isKiosk,
      cardPresentTerminals
    }),
    [
      formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      processor,
      terminalPin,
      terminalId,
      postedResponse,
      isOpen,
      isTerminalLoading,
      isCharging,
      selectedTerminal,
      isFullAppliedBalance,
      practiceId,
      terminals,
      terminalData,
      isKiosk,
      cardPresentTerminals
    ]
  );

  const contextApi = useMemo(
    () => ({
      togglePaymentMethod,
      setFieldValue: formik.setFieldValue,
      handlePayment,
      openModal,
      closeModal,
      setSelectedTerminal
    }),
    [togglePaymentMethod, handlePayment, openModal, closeModal, setSelectedTerminal]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      <PaymentContextApi.Provider value={contextApi}>{children}</PaymentContextApi.Provider>
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = () => useContext(PaymentContext);
export const usePaymentContextApi = () => useContext(PaymentContextApi);
